/**
 * ขอย้ายวันหยุดตัวเอง (มุมพนักงาน) — สลับวัน (จำนวนวันหยุดเท่าเดิม):
 *   วันหยุดเดิม → กลับมาทำงาน, วันใหม่ → หยุดแทน
 * ต้องขอล่วงหน้าอย่างน้อย 1 วัน (ทั้งสองวันต้อง >= พรุ่งนี้)
 * อนุมัติโดย HR / หัวหน้าทีม (ผ่านหน้า "อนุมัติคำขอ")
 */
import { useState } from 'react';
import { CalendarRange, Send, ArrowRight, AlertTriangle } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { useMyShiftChanges, useCreateShiftChange, useShiftOptions, type ShiftChangeRow } from '../hooks';
import { useToast } from '../components/ui/toast';
import { todayISO, toISODate } from '../lib/thai-date';

const STATUS: Record<ShiftChangeRow['status'], { label: string; cls: string }> = {
  pending: { label: 'รอหัวหน้า/HR อนุมัติ', cls: 'badge-warn' },
  approved: { label: 'อนุมัติแล้ว', cls: 'badge-success' },
  rejected: { label: 'ไม่อนุมัติ', cls: 'badge-danger' },
  cancelled: { label: 'ยกเลิก', cls: 'badge-danger' },
};

function fmtThai(iso: string): string {
  const d = new Date(`${iso}T00:00:00+07:00`);
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok' }).format(d);
}

// วันนี้/พรุ่งนี้ (ICT) แบบ YYYY-MM-DD
function todayThai(): string {
  return todayISO();
}
function tomorrowThai(): string {
  return toISODate(new Date(Date.now() + 86400_000));
}

export function ShiftChange() {
  const mine = useMyShiftChanges();
  const create = useCreateShiftChange();
  const { data: shiftOptions } = useShiftOptions();
  const toast = useToast();

  const minDate = todayThai();        // เลือกตั้งแต่วันนี้ (กระทันหันได้ แต่ห้ามย้อนหลัง)
  const tomorrow = tomorrowThai();
  const [originalDate, setOriginalDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [originalShiftId, setOriginalShiftId] = useState('');
  const [reason, setReason] = useState('');

  // กระทันหัน = แจ้งไม่ครบล่วงหน้า 1 วัน (มีวันใดวันหนึ่งเป็นวันนี้)
  const isUrgent = (!!originalDate && originalDate < tomorrow) || (!!targetDate && targetDate < tomorrow);

  function submit() {
    if (!originalDate || !targetDate) return toast.error('เลือกทั้งวันหยุดเดิมและวันใหม่');
    if (originalDate === targetDate) return toast.error('วันหยุดเดิมและวันใหม่ต้องไม่ใช่วันเดียวกัน');
    if (isUrgent && !reason.trim()) return toast.error('ย้ายวันหยุดกระทันหัน ต้องระบุเหตุผล');
    create.mutate(
      { original_date: originalDate, target_date: targetDate, original_shift_id: originalShiftId || undefined, reason: reason || undefined },
      {
        onSuccess: () => {
          toast.success(isUrgent ? 'ส่งคำขอกระทันหันแล้ว — รอ HR อนุมัติ' : 'ส่งคำขอแล้ว — รอหัวหน้า/HR อนุมัติ');
          setOriginalDate(''); setTargetDate(''); setOriginalShiftId(''); setReason('');
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'ส่งคำขอไม่สำเร็จ'),
      },
    );
  }

  // หน้านี้แสดงเฉพาะ "ย้ายวันหยุด" — คำขอเปลี่ยนกะอยู่หน้าแยก (/change-shift)
  const rows = (mine.data ?? []).filter((r) => (r.request_type ?? 'day_off_move') !== 'shift_change');

  return (
    <PageLayout title="ขอย้ายวันหยุด">
      <div className="space-y-5 pb-10">
        {/* สร้างคำขอ */}
        <div className="card p-4 space-y-3">
          <div className="font-medium text-ink flex items-center gap-1.5">
            <CalendarRange className="w-4 h-4 text-teal" /> ขอย้ายวันหยุด (สลับวัน)
          </div>
          <p className="text-xs text-gray-500">
            วันหยุดเดิมจะกลับมา <b>ทำงาน</b> และวันใหม่จะ <b>หยุดแทน</b> — จำนวนวันหยุดเท่าเดิม.
            ปกติขอ <b>ล่วงหน้าอย่างน้อย 1 วัน</b> · ถ้ากระชั้นกว่านั้น = <b>กระทันหัน</b> (ต้องมีเหตุผล, ให้ HR อนุมัติ).
          </p>
          <div>
            <label className="label">วันหยุดเดิม (จะกลับมาทำงาน)</label>
            <input
              type="date" value={originalDate} min={minDate}
              onChange={(e) => setOriginalDate(e.target.value)} className="input w-full"
            />
          </div>
          <div>
            <label className="label">กะที่จะเข้าทำงาน (วันที่กลับมาทำงาน)</label>
            <select value={originalShiftId} onChange={(e) => setOriginalShiftId(e.target.value)} className="input w-full">
              <option value="">— ใช้กะตามตารางปกติ —</option>
              {(shiftOptions ?? []).map((s) => (
                <option key={s.id} value={s.id}>{s.name} ({s.start_time}–{s.end_time})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">วันใหม่ที่จะหยุดแทน</label>
            <input
              type="date" value={targetDate} min={minDate}
              onChange={(e) => setTargetDate(e.target.value)} className="input w-full"
            />
          </div>

          {isUrgent && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span><b>คำขอกระทันหัน</b> (แจ้งไม่ครบล่วงหน้า 1 วัน) — ต้องระบุเหตุผล และจะส่งให้ <b>HR</b> อนุมัติเท่านั้น</span>
            </div>
          )}

          <div>
            <label className="label">เหตุผล{isUrgent ? ' (จำเป็นสำหรับคำขอกระทันหัน)' : ' (ถ้ามี)'}</label>
            <input
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="เช่น ติดธุระวันหยุดเดิม" className={`input w-full ${isUrgent && !reason.trim() ? 'border-amber-400' : ''}`} maxLength={300}
            />
          </div>
          <button
            onClick={submit}
            disabled={create.isPending || !originalDate || !targetDate || (isUrgent && !reason.trim())}
            className="btn-cta w-full justify-center flex items-center gap-2"
          >
            <Send className="w-4 h-4" /> {create.isPending ? 'กำลังส่ง…' : isUrgent ? 'ส่งคำขอกระทันหัน' : 'ส่งคำขอ'}
          </button>
        </div>

        {/* คำขอของฉัน */}
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">คำขอของฉัน</div>
          {mine.isLoading ? (
            <p className="text-sm text-gray-400">กำลังโหลด…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-400">ยังไม่มีคำขอ</p>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="card p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-ink flex items-center gap-1.5 flex-wrap">
                    <span>หยุด {fmtThai(r.original_date)}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                    <span>หยุด {fmtThai(r.target_date)}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {r.is_urgent && <span className="badge-warn">กระทันหัน</span>}
                    <span className={STATUS[r.status].cls}>{STATUS[r.status].label}</span>
                  </div>
                </div>
                {r.reason && <p className="text-xs text-gray-500">เหตุผล: {r.reason}</p>}
                {r.status === 'rejected' && r.rejection_reason && (
                  <p className="text-xs text-red-600">เหตุผลที่ไม่อนุมัติ: {r.rejection_reason}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </PageLayout>
  );
}
